import { useCallback, useEffect, useState } from 'react';
import {
  MfLatestSnapshot,
  MfHistoryPayload,
  MfMonthlyPayload,
} from '../types';
import {
  fetchMfFlowLatest,
  fetchMfFlowHistory,
  fetchMfFlowMonthly,
  getFallbackLatestSnapshot,
} from '../services/mutualFundFlowService';

export interface UseMutualFundDataResult {
  latest: MfLatestSnapshot | null;
  history: MfHistoryPayload | null;
  monthly: MfMonthlyPayload | null;
  loadingLatest: boolean;
  loadingDetails: boolean;
  error: string | null;
  loadFullDetails: (forceRefresh?: boolean) => Promise<void>;
  refresh: () => Promise<void>;
}

export const useMutualFundData = (): UseMutualFundDataResult => {
  const [latest, setLatest] = useState<MfLatestSnapshot | null>(null);
  const [history, setHistory] = useState<MfHistoryPayload | null>(null);
  const [monthly, setMonthly] = useState<MfMonthlyPayload | null>(null);
  const [loadingLatest, setLoadingLatest] = useState<boolean>(true);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadLatest = useCallback(async (forceRefresh = false) => {
    setLoadingLatest(true);
    setError(null);
    try {
      const data = await fetchMfFlowLatest(forceRefresh);
      setLatest(data);
    } catch (err) {
      console.error('Error fetching latest Mutual Fund flow data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load latest Mutual Fund flow data');
      setLatest((prev) => prev || getFallbackLatestSnapshot());
    } finally {
      setLoadingLatest(false);
    }
  }, []);

  const loadFullDetails = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && history && monthly) return;
    setLoadingDetails(true);
    setError(null);
    try {
      const [histData, monthData] = await Promise.all([
        fetchMfFlowHistory(forceRefresh),
        fetchMfFlowMonthly(forceRefresh),
      ]);
      setHistory(histData);
      setMonthly(monthData);
    } catch (err) {
      console.error('Error loading full Mutual Fund details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load Mutual Fund history');
    } finally {
      setLoadingDetails(false);
    }
  }, [history, monthly]);

  useEffect(() => {
    void loadLatest(false);
  }, [loadLatest]);

  const refresh = useCallback(async () => {
    await Promise.all([
      loadLatest(true),
      history || monthly ? loadFullDetails(true) : Promise.resolve(),
    ]);
  }, [loadLatest, loadFullDetails, history, monthly]);

  return {
    latest,
    history,
    monthly,
    loadingLatest,
    loadingDetails,
    error,
    loadFullDetails,
    refresh,
  };
};

export default useMutualFundData;
